import { createAppKit } from "@reown/appkit/react";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { SolanaAdapter } from "@reown/appkit-adapter-solana/react";
import { BitcoinAdapter } from "@reown/appkit-adapter-bitcoin";
import { http } from "wagmi";
import {
  mainnet, optimism, bsc, polygon, base, arbitrum, avalanche, linea, scroll,
  gnosis, moonbeam, mantle, blast, mode, metis, boba, celo, cronos,
  aurora, fantom, fraxtal, sei, pulsechain, sonic, berachain, opBNB,
  zksync, moonriver,
  solana,
  bitcoin,
} from "@reown/appkit/networks";

/** Chain catalog surfaced in the UI — matches aggregator coverage. */
export const SUPPORTED_CHAINS = [
  { id: 1, key: "ethereum", name: "Ethereum", symbol: "ETH", color: "#627EEA", icon: "https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg" },
  { id: 10, key: "optimism", name: "Optimism", symbol: "OP", color: "#FF0420", icon: "https://icons.llamao.fi/icons/chains/rsz_optimism.jpg" },
  { id: 56, key: "bsc", name: "BNB Chain", symbol: "BNB", color: "#F3BA2F", icon: "https://icons.llamao.fi/icons/chains/rsz_binance.jpg" },
  { id: 100, key: "gnosis", name: "Gnosis", symbol: "xDAI", color: "#04795B", icon: "https://icons.llamao.fi/icons/chains/rsz_xdai.jpg" },
  { id: 137, key: "polygon", name: "Polygon", symbol: "POL", color: "#8247E5", icon: "https://icons.llamao.fi/icons/chains/rsz_polygon.jpg" },
  { id: 146, key: "sonic", name: "Sonic", symbol: "S", color: "#00BFC7", icon: "https://icons.llamao.fi/icons/chains/rsz_sonic.jpg" },
  { id: 204, key: "opbnb", name: "opBNB", symbol: "BNB", color: "#F3BA2F", icon: "https://icons.llamao.fi/icons/chains/rsz_op_bnb.jpg" },
  { id: 250, key: "fantom", name: "Fantom", symbol: "FTM", color: "#1969FF", icon: "https://icons.llamao.fi/icons/chains/rsz_fantom.jpg" },
  { id: 252, key: "fraxtal", name: "Fraxtal", symbol: "frxETH", color: "#000000", icon: "https://icons.llamao.fi/icons/chains/rsz_fraxtal.jpg" },
  { id: 288, key: "boba", name: "Boba", symbol: "ETH", color: "#CBFF00", icon: "https://icons.llamao.fi/icons/chains/rsz_boba.jpg" },
  { id: 324, key: "zksync", name: "zkSync Era", symbol: "ETH", color: "#8C8DFC", icon: "https://icons.llamao.fi/icons/chains/rsz_zksync_era.jpg" },
  { id: 1088, key: "metis", name: "Metis", symbol: "METIS", color: "#00DACC", icon: "https://icons.llamao.fi/icons/chains/rsz_metis.jpg" },
  { id: 1284, key: "moonbeam", name: "Moonbeam", symbol: "GLMR", color: "#53CBC9", icon: "https://icons.llamao.fi/icons/chains/rsz_moonbeam.jpg" },
  { id: 1285, key: "moonriver", name: "Moonriver", symbol: "MOVR", color: "#F2A007", icon: "https://icons.llamao.fi/icons/chains/rsz_moonriver.jpg" },
  { id: 5000, key: "mantle", name: "Mantle", symbol: "MNT", color: "#000000", icon: "https://icons.llamao.fi/icons/chains/rsz_mantle.jpg" },
  { id: 8453, key: "base", name: "Base", symbol: "ETH", color: "#0052FF", icon: "https://icons.llamao.fi/icons/chains/rsz_base.jpg" },
  { id: 42161, key: "arbitrum", name: "Arbitrum", symbol: "ARB", color: "#28A0F0", icon: "https://icons.llamao.fi/icons/chains/rsz_arbitrum.jpg" },
  { id: 42220, key: "celo", name: "Celo", symbol: "CELO", color: "#35D07F", icon: "https://icons.llamao.fi/icons/chains/rsz_celo.jpg" },
  { id: 43114, key: "avalanche", name: "Avalanche", symbol: "AVAX", color: "#E84142", icon: "https://icons.llamao.fi/icons/chains/rsz_avalanche.jpg" },
  { id: 59144, key: "linea", name: "Linea", symbol: "ETH", color: "#61DFFF", icon: "https://icons.llamao.fi/icons/chains/rsz_linea.jpg" },
  { id: 81457, key: "blast", name: "Blast", symbol: "ETH", color: "#FCFC03", icon: "https://icons.llamao.fi/icons/chains/rsz_blast.jpg" },
  { id: 25, key: "cronos", name: "Cronos", symbol: "CRO", color: "#002D74", icon: "https://icons.llamao.fi/icons/chains/rsz_cronos.jpg" },
  { id: 34443, key: "mode", name: "Mode", symbol: "ETH", color: "#DFFE00", icon: "https://icons.llamao.fi/icons/chains/rsz_mode.jpg" },
  { id: 369, key: "pulse", name: "PulseChain", symbol: "PLS", color: "#FF41A0", icon: "https://icons.llamao.fi/icons/chains/rsz_pulse.jpg" },
  { id: 534352, key: "scroll", name: "Scroll", symbol: "ETH", color: "#FFDCB6", icon: "https://icons.llamao.fi/icons/chains/rsz_scroll.jpg" },
  { id: 80094, key: "berachain", name: "Berachain", symbol: "BERA", color: "#F47820", icon: "https://icons.llamao.fi/icons/chains/rsz_berachain.jpg" },
  { id: 1313161554, key: "aurora", name: "Aurora", symbol: "ETH", color: "#78D64B", icon: "https://icons.llamao.fi/icons/chains/rsz_aurora.jpg" },
  { id: 1329, key: "sei", name: "Sei", symbol: "SEI", color: "#8F0500", icon: "https://icons.llamao.fi/icons/chains/rsz_sei.jpg" },
  { id: 1151111081099710, key: "solana", name: "Solana", symbol: "SOL", color: "#14F195", icon: "https://icons.llamao.fi/icons/chains/rsz_solana.jpg", isSolana: true },
];

// Native token sentinel used by the underlying aggregator (0x0000…0000 = native EVM asset)
export const NATIVE_ADDRESS = "0x0000000000000000000000000000000000000000";
export const SOLANA_NATIVE = "11111111111111111111111111111111";

const REOWN_PROJECT_ID = process.env.REACT_APP_REOWN_PROJECT_ID;
if (!REOWN_PROJECT_ID) {
  // Missing project ID would silently break WalletConnect flows; fail loudly.
  // eslint-disable-next-line no-console
  console.warn("REACT_APP_REOWN_PROJECT_ID is not set — WalletConnect + Reown modal will not work.");
}

// Reliable public RPCs — the chain's public endpoint is often rate-limited or down.
const RPC = {
  1:          "https://ethereum-rpc.publicnode.com",
  10:         "https://optimism-rpc.publicnode.com",
  25:         "https://cronos-evm-rpc.publicnode.com",
  56:         "https://bsc-rpc.publicnode.com",
  100:        "https://gnosis-rpc.publicnode.com",
  137:        "https://polygon-bor-rpc.publicnode.com",
  146:        "https://rpc.soniclabs.com",
  204:        "https://opbnb-rpc.publicnode.com",
  250:        "https://fantom-rpc.publicnode.com",
  252:        "https://rpc.frax.com",
  288:        "https://mainnet.boba.network",
  324:        "https://mainnet.era.zksync.io",
  369:        "https://rpc.pulsechain.com",
  1088:       "https://andromeda.metis.io/?owner=1088",
  1284:       "https://moonbeam-rpc.publicnode.com",
  1285:       "https://moonriver-rpc.publicnode.com",
  1329:       "https://evm-rpc.sei-apis.com",
  5000:       "https://rpc.mantle.xyz",
  8453:       "https://base-rpc.publicnode.com",
  34443:      "https://mainnet.mode.network",
  42161:      "https://arbitrum-one-rpc.publicnode.com",
  42220:      "https://celo-rpc.publicnode.com",
  43114:      "https://avalanche-c-chain-rpc.publicnode.com",
  59144:      "https://linea-rpc.publicnode.com",
  80094:      "https://rpc.berachain.com",
  81457:      "https://blast-rpc.publicnode.com",
  534352:     "https://scroll-rpc.publicnode.com",
  1313161554: "https://mainnet.aurora.dev",
};

const EVM_CHAINS = [
  mainnet, optimism, bsc, polygon, base, arbitrum, avalanche, linea, scroll,
  gnosis, moonbeam, mantle, blast, mode, metis, boba, celo, cronos,
  aurora, fantom, fraxtal, sei, pulsechain, sonic, berachain, opBNB,
  zksync, moonriver,
];

// All AppKit-supported ecosystems in a single unified modal.
export const APPKIT_NETWORKS = [...EVM_CHAINS, solana, bitcoin];

const SOLANA_RPC = process.env.REACT_APP_SOLANA_RPC || "https://solana-rpc.publicnode.com";

// Wagmi adapter — replaces RainbowKit's connector list with WalletConnect + injected
// wallets across every browser, plus Coinbase, Rabby, Trust, Brave via EIP-6963.
export const wagmiAdapter = new WagmiAdapter({
  projectId: REOWN_PROJECT_ID,
  networks: EVM_CHAINS,
  transports: Object.fromEntries(EVM_CHAINS.map((c) => [c.id, http(RPC[c.id])])),
  ssr: false,
});

const solanaAdapter = new SolanaAdapter({
  // Auto-registers Phantom, Solflare, Backpack, Glow, Magic Eden, etc. via Wallet Standard.
  registerWalletStandard: true,
});

const bitcoinAdapter = new BitcoinAdapter({ projectId: REOWN_PROJECT_ID });

// Create the unified modal once at module scope.
export const appKit = createAppKit({
  projectId: REOWN_PROJECT_ID,
  adapters: [wagmiAdapter, solanaAdapter, bitcoinAdapter],
  networks: APPKIT_NETWORKS,
  defaultNetwork: mainnet,
  customRpcUrls: {
    [`solana:${solana.id}`]: [{ url: SOLANA_RPC }],
  },
  themeMode: "light",
  themeVariables: {
    "--w3m-accent": "#4F46E5",
    "--w3m-border-radius-master": "3px",
    "--w3m-font-family": "Manrope, system-ui, sans-serif",
  },
  metadata: {
    name: "DEX Router",
    description: "Cross-chain DEX aggregator — EVM, Solana, Sui, Tron, Bitcoin in one swap",
    url: typeof window !== "undefined" ? window.location.origin : "https://decentralizedexchange.org",
    icons: ["https://decentralizedexchange.org/favicon.png"],
  },
  features: {
    analytics: false,
    email: false,
    socials: [],
    onramp: false,
    swaps: false,
  },
});

// Consumed by <WagmiProvider> in index.js so wagmi hooks (useAccount,
// useSendTransaction, etc.) keep working exactly as before.
export const wagmiConfig = wagmiAdapter.wagmiConfig;
